import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { compPlanStatementSelector } from "src/redux/selectors/custom-selector";
import { Grid, GridColumn as Column, GridToolbar, GridNoRecords } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { setExpandedState, setGroupIds } from "@progress/kendo-react-data-tools";

import { IntlService } from "@progress/kendo-react-intl";
import { Button } from "@progress/kendo-react-buttons";
import { setCompPlanSection2DataAction } from "src/redux/actions/compplan-actions";
import { Tooltip } from "@progress/kendo-react-tooltip";
import CustomMultiSelect from "../../components/multiSelect";
import { filterPeriods } from "../../utils/utils";
import { LoadingPanel } from "src/components/loadingPanel";

import ExcelExportCompPlanStatement from "./ExcelExportCompPlanStatement";
const intl = new IntlService("en");

const getEyeIcon = () => {
  return <img src={require("../../images/eye-open.svg").default} alt="Logo" width={11} />;
};

const initialDataState = {
  group: [
    {
      field: "kpi_name",
      compare: (a, b) => a.kpi_report_order - b.kpi_report_order,
    },
    { field: "result_group_name", dir: "asc" },
    { field: "result_type_name", dir: "asc" },
  ],
};
const aggregates = [
  {
    field: "period1_value_total",
    aggregate: "sum",
  },
  {
    field: "period2_value_total",
    aggregate: "sum",
  },
  {
    field: "period3_value_total",
    aggregate: "sum",
  },
  {
    field: "period4_value_total",
    aggregate: "sum",
  },
  {
    field: "period5_value_total",
    aggregate: "sum",
  },
  {
    field: "period6_value_total",
    aggregate: "sum",
  },
  {
    field: "period7_value_total",
    aggregate: "sum",
  },
  {
    field: "period8_value_total",
    aggregate: "sum",
  },
  {
    field: "period9_value_total",
    aggregate: "sum",
  },
  {
    field: "period10_value_total",
    aggregate: "sum",
  },
  {
    field: "period11_value_total",
    aggregate: "sum",
  },
  {
    field: "period12_value_total",
    aggregate: "sum",
  },
  {
    field: "period13_value_total",
    aggregate: "sum",
  },
  {
    field: "period14_value_total",
    aggregate: "sum",
  },
  {
    field: "period15_value_total",
    aggregate: "sum",
  },
  {
    field: "period16_value_total",
    aggregate: "sum",
  },
  {
    field: "period17_value_total",
    aggregate: "sum",
  },
];

const quarterColumns = ["period4_value", "period8_value", "period12_value", "period16_value"];
const yearColumn = "period17_value";
const monthColumns = [
  "period1_value",
  "period2_value",
  "period3_value",
  "period5_value",
  "period6_value",
  "period7_value",
  "period9_value",
  "period10_value",
  "period11_value",
  "period13_value",
  "period14_value",
  "period15_value",
];

const processWithGroups = (data, dataState) => {
  const groups = dataState.group;
  if (groups) {
    groups.map((group) => (group.aggregates = aggregates));
  }
  dataState.group = groups;
  const newDataState = process(data, dataState);

  setGroupIds({
    data: newDataState.data,
    group: dataState.group,
  });
  return newDataState;
};

const CompPlanSectionTwo = forwardRef((props, sectionRef) => {
  const {
    isReportRunning,
    isExporting,
    reportHeight,
    systemReportPeriodInfo,
    userPrefConfigInfo,
    userPrefColumnsList,
    isPeriodDataLoaded,
  } = props;
  const { compPlanSection2Data, isProviderDetail, showSection1, advanceFilterSelections } = useSelector(
    compPlanStatementSelector,
    shallowEqual
  );
  const dispatch = useDispatch();

  const effectiveProviderDetail =
    advanceFilterSelections !== null ? (advanceFilterSelections?.isProviderDetail ?? false) : isProviderDetail;

  const dataState = useMemo(() => {
    return effectiveProviderDetail
      ? {
          ...initialDataState,
          group: [
            { field: "position_name" },
            {
              field: "kpi_name",
              compare: (a, b) => a.kpi_report_order - b.kpi_report_order,
            },
            { field: "result_group_name" },
            { field: "result_type_name", dir: "asc" },
          ],
        }
      : initialDataState;
  }, [effectiveProviderDetail]);

  const [result, setResult] = React.useState(processWithGroups(compPlanSection2Data.data, dataState));
  const [collapsedState, setCollapsedState] = React.useState([]);
  const excelExportRef = React.useRef(null);

  const periodFilList = systemReportPeriodInfo?.input_control_values
    ? JSON.parse(systemReportPeriodInfo?.input_control_values) || []
    : [];
  const periodFilterList = [{ key: 0, value: "Select All" }, ...periodFilList];

  const [selectedPeriods, setSelectedPeriods] = React.useState([]);
  const [activePeriodList, setActivePeriodList] = React.useState(periodFilList || []);

  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [hasDataLoaded, setHasDataLoaded] = React.useState(false);
  const debounceRef = React.useRef(null);
  const latestSelectedListRef = React.useRef("");
  const periodListRef = React.useRef("");
  const periodDebounceRef = React.useRef(null);
  const isPeriodChangedRef = React.useRef(false);

  const periodList = React.useMemo(() => {
    if (!compPlanSection2Data?.columns.length || selectedPeriods.length === 0) return [];

    let periodFilters = selectedPeriods.map((it) => Number(it.key));
    if (!periodFilters.length) periodFilters = [0]; // Default to show all if none valid

    const periods = filterPeriods(compPlanSection2Data?.columns, periodFilters);

    return periods;
  }, [compPlanSection2Data?.columns, selectedPeriods]);

  useEffect(() => {
    if (!isPeriodDataLoaded) return;
    const selectedData = periodFilterList.filter((it) => {
      const perNum = Number(it.key);
      const periodFilters = (
        (userPrefConfigInfo && userPrefConfigInfo?.user_pref_value) ||
        systemReportPeriodInfo?.pref_value
      )
        ?.toString()
        ?.split(",")
        ?.map((it) => Number(it.trim()));

      return periodFilters?.length ? periodFilters.includes(perNum) : [0].includes(perNum);
    });
    setSelectedPeriods(selectedData);
    periodListRef.current = userPrefConfigInfo?.user_pref_value || systemReportPeriodInfo?.pref_value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrefConfigInfo?.user_pref_value, isPeriodDataLoaded]);

  useEffect(() => {
    if (userPrefColumnsList) {
      latestSelectedListRef.current = userPrefColumnsList;
      setHasDataLoaded(true);
    }
  }, [userPrefColumnsList]);

  const columnList = React.useMemo(() => {
    if (!compPlanSection2Data?.columns?.length || !hasDataLoaded || !isPeriodDataLoaded) {
      return [];
    }
    if (isPeriodChangedRef.current) {
      isPeriodChangedRef.current = false;
      return [];
    }

    let columns = [];
    if (latestSelectedListRef?.current !== "") {
      const visibleColumns = new Set(latestSelectedListRef?.current?.split(","));
      columns = compPlanSection2Data?.columns.map((col) => ({
        ...col,
        show: visibleColumns.has(col.per_number.toString()),
      }));
    } else if (isInitialLoad && latestSelectedListRef.current !== "") {
      columns = compPlanSection2Data?.columns;
    }
    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanSection2Data?.columns, hasDataLoaded, isPeriodDataLoaded]);

  useEffect(() => {
    if (periodList.length && !isInitialLoad) {
      dispatch(setCompPlanSection2DataAction({ columns: periodList }));
      handleColumnChange(periodList);
    } else if (
      isInitialLoad &&
      periodList.length > 0 &&
      !hasDataLoaded &&
      latestSelectedListRef.current === "" &&
      isPeriodDataLoaded
    ) {
      dispatch(setCompPlanSection2DataAction({ columns: periodList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(periodList), isPeriodDataLoaded]);

  useEffect(() => {
    if (columnList.length > 0) {
      dispatch(setCompPlanSection2DataAction({ columns: columnList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(columnList)]);

  useEffect(() => {
    return () => {
      if (periodDebounceRef?.current && periodListRef?.current !== "") {
        props.updateUserPrefConfigInfo(periodListRef?.current);
        clearTimeout(periodDebounceRef.current);
        periodDebounceRef.current = null;
        periodListRef.current = "";
      }
      if (debounceRef?.current && latestSelectedListRef?.current !== "") {
        props.onUpdateUserPrefColumnsList(latestSelectedListRef?.current);
        if ((!userPrefConfigInfo || !userPrefConfigInfo?.user_pref_value) && isInitialLoad) {
          props.updateUserPrefConfigInfo(periodListRef?.current);
        }
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        latestSelectedListRef.current = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allowToggle = useMemo(() => {
    return selectedPeriods.some((it) => [0, 1].includes(Number(it.key))); // If month or all selected
  }, [selectedPeriods]);

  const hasQuarterShown = useMemo(() => {
    return selectedPeriods.some((it) => [0, 2].includes(Number(it.key)));
  }, [selectedPeriods]);

  const hasYearShown = useMemo(() => {
    return selectedPeriods.some((it) => [0, 4].includes(Number(it.key)));
  }, [selectedPeriods]);

  const lastVisibleColumn = useMemo(() => {
    // Find last visible month column (excluding year and last month)
    let lastVisibleMonthColPerNumber = -1;
    const visibleMonthCols = compPlanSection2Data?.columns.filter(
      (col) => col.show && `period${col.per_number}_value` !== yearColumn
    );
    if (visibleMonthCols?.length)
      lastVisibleMonthColPerNumber = visibleMonthCols[visibleMonthCols.length - 1].per_number;

    // Check if last visible month column and next columns are hidden
    let checkIfLastHidden = false;
    if (lastVisibleMonthColPerNumber !== -1) {
      // If quarters are shown, check both month 15 and quarter 16
      if (hasQuarterShown) {
        checkIfLastHidden =
          compPlanSection2Data?.columns[lastVisibleMonthColPerNumber]?.show === false &&
          compPlanSection2Data?.columns[15]?.show === false;
      } else {
        // Only check month 15
        checkIfLastHidden =
          compPlanSection2Data?.columns[lastVisibleMonthColPerNumber]?.show === false &&
          compPlanSection2Data?.columns[14]?.show === false;
      }
    }

    return { checkIfLastHidden, lastVisibleMonthColPerNumber };
  }, [hasQuarterShown, compPlanSection2Data?.columns]);

  const isYearColumnHidden = useMemo(() => {
    return (
      yearColumn &&
      compPlanSection2Data?.columns.find((col) => `period${col.per_number}_value` === yearColumn)?.show === false &&
      selectedPeriods.some((it) => [0, 4].includes(Number(it.key)))
    );
  }, [compPlanSection2Data?.columns, selectedPeriods]);

  const isOnlyOneColumnVisible = useMemo(() => {
    const visibleCols = compPlanSection2Data?.columns
      .filter(
        (col) =>
          monthColumns.includes(`period${col.per_number}_value`) ||
          quarterColumns.includes(`period${col.per_number}_value`) ||
          `period${col.per_number}_value` === yearColumn
      )
      .filter((col) => col.show === true);
    return visibleCols.length === 1;
  }, [compPlanSection2Data?.columns]);

  const handleReportPeriodChange = (value) => {
    let items;
    // If all periods except "Select All" (key 0) are selected, treat as "Select All"
    if (value.length === periodFilterList.length - 1 && !value.some((it) => it.key === 0)) {
      items = [periodFilterList[0]];
    } else if (value.length === 0 || value.findIndex((it) => it.key === 0) > 0) {
      items = [periodFilterList[0]];
    } else {
      items = value.filter((it) => it.key !== 0);
    }

    setSelectedPeriods(items);
    const values = items.map((it) => Number(it.key)).join(",");

    if (periodListRef?.current !== values) {
      periodListRef.current = values;
      isPeriodChangedRef.current = true;
      setIsInitialLoad(false);
      // Clear timer
      if (periodDebounceRef?.current) clearTimeout(periodDebounceRef.current);

      // Restart debounce
      periodDebounceRef.current = setTimeout(() => {
        props.updateUserPrefConfigInfo(values);

        // Clear timeout after call
        if (periodDebounceRef.current) {
          clearTimeout(periodDebounceRef.current);
          periodDebounceRef.current = null;
        }
      }, 2000);
    }
  };

  const expandChange = (event) => {
    const item = event.dataItem;
    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }

    setTimeout(() => {
      resizeColGroup();
    }, 0);
  };

  const newData = setExpandedState({
    data: result.data,
    collapsedIds: collapsedState,
  });

  const handleColumnChange = (finalGrids) => {
    const visbleList = finalGrids
      .map((col) => (col.show ? col.per_number : null))
      .filter((it) => it !== null)
      .join(",");
    if (latestSelectedListRef?.current !== visbleList) {
      latestSelectedListRef.current = visbleList;
      // Clear timer
      if (debounceRef?.current) clearTimeout(debounceRef.current);

      // Restart debounce
      debounceRef.current = setTimeout(() => {
        props.onUpdateUserPrefColumnsList(visbleList);
        if ((!userPrefConfigInfo || !userPrefConfigInfo?.user_pref_value) && isInitialLoad) {
          props.updateUserPrefConfigInfo(periodListRef?.current);
        }

        // Clear timeout after call
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
      }, 2000);
    }
  };

  const cellRender = (tdElement, cellProps) => {
    if (tdElement && cellProps.rowType === "groupHeader") {
      const rowStyle = {
        position: "sticky",
        fontWeight: "bold",
        backgroundColor:
          cellProps.dataItem.field === "position_name"
            ? "#009DDC"
            : cellProps.dataItem.field === "kpi_name"
              ? "#7AC143"
              : cellProps.dataItem.field === "result_group_name"
                ? "#eae8e8"
                : cellProps.dataItem.field === "result_type_name"
                  ? "#FDFD96"
                  : undefined,
      };
      const valDisplay = tdElement.props.children?.props?.children?.[1];

      const headerVal =
        tdElement.props.children?.type === "p" ? (
          <>
            {tdElement.props.children?.props?.children?.[0]}
            <span title={valDisplay}>{valDisplay}</span>
          </>
        ) : null;
      return React.cloneElement(
        tdElement,
        {
          ...tdElement.props,
          style: { ...tdElement.props.style, ...rowStyle },
        },
        headerVal
      );
    } else if (tdElement && cellProps.rowType === "data" && cellProps.field.startsWith("period")) {
      const dataVal = parseVal(cellProps.dataItem[cellProps.field], cellProps.dataItem.report_format);
      const dataVal1 = <span title={dataVal}>{dataVal}</span>;

      const quarterColClass = quarterColumns.includes(cellProps.field) ? "quarter-data-cell" : "";
      const yearColClass = cellProps.field === yearColumn ? " year-data-cell" : "";

      return React.cloneElement(
        tdElement,
        { ...tdElement.props, className: `${tdElement.props.className || ""} ${quarterColClass} ${yearColClass}` },
        dataVal1
      );
    } else if (tdElement && cellProps.rowType === "data") {
      const dataVal = <span title={cellProps.dataItem[cellProps.field]}>{cellProps.dataItem[cellProps.field]}</span>;
      return React.cloneElement(tdElement, tdElement.props, dataVal);
    }
    return tdElement;
  };
  const rowRender = (row, props) => {
    if (props.rowType === "groupHeader" && !props.dataItem.value) return null;
    return row;
  };

  const CustomHeaderCell = (props) => {
    // Helper: get column index and info
    const colIndex = compPlanSection2Data?.columns.findIndex((col) => `period${col.per_number}_value` === props.field);

    // Period selection helpers
    const isQuarterColumn = quarterColumns.includes(props.field);
    const isMonthColumn = monthColumns.includes(props.field);

    // Previous column hidden logic
    let isPreviousColumnHidden = false;
    if (colIndex > 0) {
      let prevIdx = colIndex - 1;
      if (!hasQuarterShown) {
        // Skip quarter columns
        while (
          prevIdx >= 0 &&
          quarterColumns.includes(`period${compPlanSection2Data?.columns[prevIdx]?.per_number}_value`)
        )
          prevIdx--;
      }
      if (!allowToggle) {
        // Skip month columns
        while (
          prevIdx >= 0 &&
          monthColumns.includes(`period${compPlanSection2Data?.columns[prevIdx]?.per_number}_value`)
        )
          prevIdx--;
      }
      if (prevIdx >= 0) {
        isPreviousColumnHidden = compPlanSection2Data?.columns[prevIdx]?.show === false;
      }
    }

    const showIcon = hasQuarterShown ? [15].includes(colIndex) : [14, 15].includes(colIndex);
    const yearColumnData =
      isYearColumnHidden && showIcon && !isExporting ? (
        <span className="year-span" onClick={showYearColumn} title="Show Year">
          <i className="fa fa-caret-right fa-s"></i>
        </span>
      ) : null;

    const ifItsLastVisibleColumn =
      colIndex <= compPlanSection2Data?.columns[compPlanSection2Data?.columns.length - 1]?.per_number - 1;
    const hasRightCaret = ifItsLastVisibleColumn && (isYearColumnHidden || !hasYearShown);

    // Render for month columns with show/hide
    if (isMonthColumn || isQuarterColumn || ((hasQuarterShown || allowToggle) && isOnlyOneColumnVisible)) {
      return (
        <span className="k-cell-inner" title={props.title}>
          <span className="k-link k-text-center">
            {isPreviousColumnHidden && !isExporting && (
              <span className="month-span" onClick={() => hideShowColumns(props.field, true)} title="Show">
                <i className="fas fa-caret-left"></i>
              </span>
            )}
            <Button
              className={`k-column-title parent-button cursor-default${props.field === yearColumn ? " override-bg" : ""} 
              ${isQuarterColumn ? " quarter-btn" : ""}`}
              fillMode="solid"
            >
              <span
                title={props.title}
                className={`column-font11 ${isQuarterColumn && allowToggle ? "cursor-pointer" : ""}`}
                onClick={() => isQuarterColumn && allowToggle && toggleColumn(props.field)}
              >
                {props.title}
              </span>
              {!isExporting && !isOnlyOneColumnVisible && (
                <span title="Hide" className="hide-eye-icon" onClick={() => hideShowColumns(props.field, false)}>
                  {getEyeIcon()}
                </span>
              )}
            </Button>
            {lastVisibleColumn.lastVisibleMonthColPerNumber - 1 === colIndex &&
            lastVisibleColumn.checkIfLastHidden &&
            !isExporting ? (
              <span
                className={hasRightCaret ? "caret-span" : "left-arrow-span"}
                onClick={() => hideShowColumns(props.field, true, true, isYearColumnHidden)}
                title="Show"
              >
                <i className={`fas fa-caret-${hasRightCaret ? "right" : "left"}`}></i>
              </span>
            ) : (
              yearColumnData
            )}
          </span>
        </span>
      );
    }

    // Default render for other columns
    return (
      <span
        className={`k-cell-inner${props.field === yearColumn ? " override-bg" : ""}${isQuarterColumn ? " override-bg-quarter" : ""}`}
        title={props.title}
      >
        <span className="k-link k-text-center">
          <Button
            className={`k-column-title parent-button ${props.field === yearColumn && "year-style"}`}
            fillMode="flat"
            style={{ color: "#424242", cursor: "default" }}
          >
            <span title={props.title}>{props.title}</span>
            {props.field === yearColumn &&
              (allowToggle || hasQuarterShown) &&
              !isExporting &&
              !isOnlyOneColumnVisible && (
                <span title="Hide" className="hide-year-eye-icon" onClick={() => hideShowColumns(props.field, false)}>
                  {getEyeIcon()}
                </span>
              )}
          </Button>
          <div className="year-column-container">{yearColumnData}</div>
        </span>
      </span>
    );
  };

  const getTotalValue = (field) => {
    let format = "$#,##0.00;-$#,##0.00";
    const item = compPlanSection2Data.data.filter((it) => it.result_metadata_tags === "<grand_total>");
    if (item.length) {
      format = item[0].report_format;
    }

    const total = item.reduce((it, it1) => it + it1[`${field}_total`], 0);
    const numberVal = intl.parseNumber(total || 0);
    return intl.formatNumber(numberVal, format);
  };

  const parseVal = (fieldVal, format) => {
    if (typeof fieldVal === "string" && isNaN(Number(fieldVal))) {
      return fieldVal;
    }
    const numberVal = intl.parseNumber(fieldVal || 0);

    if (!format) {
      return !fieldVal ? "" : Boolean(numberVal) ? "True" : "False";
    }

    const formatArr = format.split("-");
    return intl.formatNumber(numberVal, formatArr[formatArr.length - 1]);
  };

  const toggleColumn = (field) => {
    let ids = field.match(/\d+/g).map(Number);
    if (!ids.length) return;

    const parNumber = ids[0];
    let finalGrids = [...compPlanSection2Data?.columns];
    // Check if any of the previous 2 to 4 columns are shown
    let anyShown = false;
    for (let i = 2; i <= 4; i++) {
      if (finalGrids[parNumber - i] && finalGrids[parNumber - i].show === true) {
        anyShown = true;
        break;
      }
    }
    // If any are shown, set all to false; otherwise, set all to true
    for (let i = 2; i <= 4; i++) {
      if (finalGrids[parNumber - i]) {
        finalGrids[parNumber - i] = {
          ...finalGrids[parNumber - i],
          show: !anyShown,
        };
      }
    }

    dispatch(setCompPlanSection2DataAction({ columns: finalGrids }));
    handleColumnChange(finalGrids);
  };

  const hideShowColumns = (field, show, isLastMonthsHidden = false, isYearColumnHidden = false) => {
    const ids = field.match(/\d+/g)?.map(Number);
    if (!ids?.length) return;

    const parNumber = ids[0];
    const finalGrids = [...compPlanSection2Data?.columns];
    const breakdowns = [4, 8, 12, 16];

    if (isLastMonthsHidden) {
      // Show all remaining month columns after the current field index
      for (let i = parNumber; i < 16; i++) {
        const colField = `period${i + 1}_value`;
        if (monthColumns.includes(colField)) {
          if (!allowToggle) continue;
          finalGrids[i] = { ...finalGrids[i], show: true };
        } else if (quarterColumns.includes(colField)) {
          if (!hasQuarterShown) continue;
          finalGrids[i] = { ...finalGrids[i], show: true };
        } else {
          break;
        }
      }
      if (isYearColumnHidden) {
        showYearColumn(finalGrids);
      } else {
        dispatch(setCompPlanSection2DataAction({ columns: finalGrids }));
        handleColumnChange(finalGrids);
      }
      return;
    }

    if (show) {
      // Show all previous hidden columns until a breakdown or a visible column is found
      for (let i = parNumber - 2; i >= 0; i--) {
        if (!hasQuarterShown && breakdowns.includes(i + 1)) continue;
        if (!allowToggle && monthColumns.includes(`period${i + 1}_value`)) continue;
        if (finalGrids[i]?.show === false) {
          finalGrids[i] = { ...finalGrids[i], show: true };
        } else {
          break;
        }
      }
    } else {
      // Hide current column
      finalGrids[parNumber - 1] = { ...finalGrids[parNumber - 1], show: false };
    }

    dispatch(setCompPlanSection2DataAction({ columns: finalGrids }));
    handleColumnChange(finalGrids);
  };

  const showYearColumn = (finalGridsData = []) => {
    let finalGrids = finalGridsData?.length > 0 ? [...finalGridsData] : [...compPlanSection2Data?.columns];
    const yearColIndex = finalGrids.findIndex((col) => `period${col.per_number}_value` === "period17_value");
    if (yearColIndex >= 0) {
      finalGrids[yearColIndex] = {
        ...finalGrids[yearColIndex],
        show: true,
      };
      dispatch(setCompPlanSection2DataAction({ columns: finalGrids }));
      handleColumnChange(finalGrids);
    }
  };

  useEffect(() => {
    const newDataState = processWithGroups(compPlanSection2Data.data, dataState);
    setResult(newDataState);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanSection2Data.data]);

  useEffect(() => {
    resizeColGroup();

    if (document.getElementsByClassName("k-grouping-header").length > 0) {
      const element = document.getElementsByClassName("k-grouping-header").item(0);
      element.classList.add("k-hidden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanSection2Data, isReportRunning]);

  const resizeColGroup = () => {
    const colGroup = document.getElementsByTagName("colgroup");

    if (colGroup?.length) {
      for (let i = 0; i < colGroup.length; i++) {
        const colGroupCol = colGroup[i].getElementsByTagName("col");
        for (let j = 0; j < colGroupCol.length; j++) {
          if (colGroupCol[j].offsetWidth > 10 && colGroupCol[j].offsetWidth < 35) {
            colGroupCol[j].width = "10px";
          }
        }
      }
    }
  };

  const TooltipContentTemplate = (props) => {
    const targetObj = props.title;
    const title = targetObj.title;

    return (
      <div className="pc-grid-tooltip">
        <strong>{title}</strong>
      </div>
    );
  };

  const isEllipsisActive = (element) => {
    let returnBool = false;

    const allowTags = ["SPAN", "TD"];

    if (!allowTags.includes(element.tagName)) {
      return returnBool;
    }

    let tdElement = element;
    let spanElement = element;

    if (element.tagName === "TD") {
      spanElement = element.firstChild;
    } else {
      tdElement = element.closest("td");
    }

    if (!tdElement || !spanElement) {
      return returnBool;
    }

    if (spanElement?.className?.includes("k-icon")) {
      return returnBool;
    }

    const bodyStyle = window.getElementStyle(tdElement);
    const tdPadding = parseFloat(bodyStyle["paddingLeft"]) + parseFloat(bodyStyle["paddingRight"]);

    const tdWidth = tdElement.offsetWidth - tdPadding;
    const spanWidth = spanElement.offsetWidth;

    // text was truncated.
    if (spanWidth > tdWidth) {
      spanElement.title = spanElement.innerText;
      returnBool = true;
    } else {
      spanElement.title = "";
    }

    return returnBool;
  };

  useImperativeHandle(sectionRef, () => {
    return {
      exportToExcel: () => excelExportRef.current?.excelExport(),
      getWorkbookOptions: () => excelExportRef.current?.getWorkbookOptions(),
      saveWorkbook: (workbook, fileName) => excelExportRef.current?.saveWorkbook(workbook, fileName),
    };
  }, []);

  if (compPlanSection2Data.columns.length === 0 || !isPeriodDataLoaded) return <LoadingPanel />;

  return (
    <Tooltip
      content={(props) => <TooltipContentTemplate title={props} />}
      filter={isEllipsisActive}
      openDelay={100}
      position="auto"
      //anchorElement="target"
    >
      <ExcelExportCompPlanStatement newData={newData} group={dataState.group} ref={excelExportRef} />
      <Grid
        style={
          isExporting || isReportRunning
            ? {
                height: "100%",
                minHeight: isReportRunning ? reportHeight : undefined,
              }
            : {
                height: reportHeight,
                overflowY: "scroll",
              }
        }
        className="plan-comp-section2 kpipaymentgridpadding plan-columns"
        resizable={false}
        groupable={{ enabled: true, footer: "none" }}
        data={newData}
        {...dataState}
        scrollable={isExporting ? "scrollable" : "scrollable"}
        lockGroups={true}
        onExpandChange={expandChange}
        expandField="expanded"
        cellRender={cellRender}
        rowRender={rowRender}
      >
        <GridNoRecords>
          <span>{!isReportRunning ? "No Data Available" : undefined}</span>
        </GridNoRecords>
        <GridToolbar>
          <div className="Page-title">
            <h1>KPI Payment Details</h1>
          </div>
          {!isExporting && (
            <div className={"k-form-field-wrap-popup valueDropdown periodDropdown"}>
              <CustomMultiSelect
                name="groupDropdown"
                data={activePeriodList}
                textField="value"
                dataItemKey="key"
                valueData={selectedPeriods}
                onChange={(value) => handleReportPeriodChange(value)}
                dataList={periodFilterList?.filter((it) => it.key !== 0)}
                filterable={true}
                isSelectALl={false}
                style={{ maxHeight: 200 }}
                onFilterChange={(dataValue) => setActivePeriodList(dataValue)}
                autoClose={false}
                onClose={() => {
                  setActivePeriodList(periodFilterList?.filter((it) => it.key !== 0));
                }}
              />
              <Tooltip position="auto" openDelay={100}>
                <span
                  title="Filter data by time period. Select 'Select All' to view all periods, or choose 'Year', 'Quarter', or 'Month' to display data for that specific interval."
                  className="fas fa-info infoIcon infoTooltip"
                ></span>
              </Tooltip>
            </div>
          )}
        </GridToolbar>
        <Column
          field="result_name"
          title="Result Name"
          width={180}
          resizable={false}
          headerCell={CustomHeaderCell}
          locked
          footerCell={(props) =>
            !isReportRunning || isExporting ? (
              <td className="k-text-center" colSpan={props.colSpan} style={props.style}>
                <span title="Total Payments">Total Payments</span>
              </td>
            ) : null
          }
        />
        {compPlanSection2Data.columns
          .filter((it) => it.show)
          .map((it, index) => (
            <Column
              id={it.per_number}
              key={it.per_number}
              className="k-text-center"
              width={it.period_name.toLowerCase().includes("year") ? 90 : 85}
              resizable={false}
              field={`period${it.per_number}_value`}
              title={it.period_name}
              headerCell={CustomHeaderCell}
              footerCell={(props) =>
                !isReportRunning || isExporting ? (
                  <td className="k-text-center" colSpan={props.colSpan} style={props.style}>
                    <span title={getTotalValue(`period${it.per_number}_value`)}>
                      {getTotalValue(`period${it.per_number}_value`)}
                    </span>
                  </td>
                ) : null
              }
            />
          ))}
      </Grid>
    </Tooltip>
  );
});

export default React.memo(CompPlanSectionTwo);
